// Admin: print-agent fleet.  Lists every agent + lets the admin create
// new ones.  On create the raw bearer token is shown once in a modal —
// after that it's only sha256(token) in DB and can't be read back; use
// rotate-token on the detail page to issue a new one.

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import type { TFunction } from "i18next";
import { Link } from "react-router-dom";

import { ErrorBanner } from "@/components/ErrorBanner";
import { PageHeader } from "@/components/PageHeader";
import { RequirePermission } from "@/components/RequirePermission";
import { ErrorState, LoadingState } from "@/components/States";
import {
  printAgentsAdminApi,
  type PrintAgentCreateResponse,
  type PrintAgentPrinter,
  type PrintAgentRead,
} from "@/lib/api";

const ONLINE_FRESH_SECONDS = 60;

export default function PrintAgentsList() {
  return (
    <RequirePermission codes="print.agent.manage">
      <Inner />
    </RequirePermission>
  );
}

function Inner() {
  const { t } = useTranslation();
  const [creating, setCreating] = useState(false);
  const [created, setCreated] = useState<PrintAgentCreateResponse | null>(null);

  const q = useQuery({
    queryKey: ["admin", "print-agents"],
    queryFn: () => printAgentsAdminApi.list(),
    refetchInterval: 15_000,
  });

  return (
    <>
      <PageHeader
        eyebrow={t("eyebrows.adminPrint")}
        title={t("printAgents.title")}
        description={t("printAgents.description")}
        actions={
          <button className="btn btn-primary" onClick={() => setCreating(true)}>
            {t("printAgents.newAgent")}
          </button>
        }
      />

      {q.isLoading && <LoadingState />}
      {q.isError && <ErrorState error={q.error} />}

      {q.data && q.data.items.length === 0 && (
        <section className="card p-8 text-center text-text-muted">
          <p>{t("printAgents.empty")}</p>
          <p className="text-xs mt-2">
            {t("printAgents.emptyHint")}
          </p>
        </section>
      )}

      {q.data && q.data.items.length > 0 && (
        <section className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mt-4">
          {q.data.items.map((a) => (
            <AgentCard key={a.id} a={a} />
          ))}
        </section>
      )}

      {creating && (
        <CreateModal
          onClose={() => setCreating(false)}
          onCreated={(resp) => {
            setCreating(false);
            setCreated(resp);
          }}
        />
      )}
      {created && <TokenModal data={created} onClose={() => setCreated(null)} />}
    </>
  );
}

function AgentCard({ a }: { a: PrintAgentRead }) {
  const { t } = useTranslation();
  const online = isOnline(a);
  return (
    <Link
      to={`/admin/print-agents/${a.id}`}
      className="card p-5 hover:border-accent transition-colors block"
    >
      <header className="flex items-start justify-between gap-2">
        <div className="min-w-0 flex-1">
          <h3 className="text-base truncate">{a.name}</h3>
          <p className="text-text-muted text-xs font-mono mt-1">{a.id.slice(0, 8)}…</p>
        </div>
        <span
          className={
            "pill font-mono text-xs whitespace-nowrap " +
            (!a.is_active ? "pill-warn" : online ? "pill-ok" : "pill-warn")
          }
        >
          {!a.is_active ? t("printAgents.off") : online ? "online" : "offline"}
        </span>
      </header>
      <p className="text-text-muted text-xs mt-3">
        {t("printAgents.printersCount", { count: a.printers.length })}
        {a.last_seen_at && (
          <> · {t("printAgents.lastContact", { time: formatRelative(a.last_seen_at, t) })}</>
        )}
      </p>
    </Link>
  );
}

function CreateModal({
  onClose,
  onCreated,
}: {
  onClose: () => void;
  onCreated: (resp: PrintAgentCreateResponse) => void;
}) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [name, setName] = useState("");
  const [printers, setPrinters] = useState<PrintAgentPrinter[]>([
    { name: "laser-bw", handles_kinds: ["passport", "packing-list", "op-token-A4"] },
    { name: "label-sticker", handles_kinds: ["item-tag-sticker", "label-L"] },
  ]);
  const [err, setErr] = useState<string | null>(null);

  const mut = useMutation({
    mutationFn: () =>
      printAgentsAdminApi.create({
        name: name.trim(),
        printers: printers
          .filter((p) => p.name.trim())
          .map((p) => ({
            name: p.name.trim(),
            handles_kinds: p.handles_kinds.filter(Boolean),
          })),
      }),
    onSuccess: (resp) => {
      void qc.invalidateQueries({ queryKey: ["admin", "print-agents"] });
      onCreated(resp);
    },
    onError: (e) => setErr(e instanceof Error ? e.message : t("common.error")),
  });

  const updatePrinter = (idx: number, patch: Partial<PrintAgentPrinter>) =>
    setPrinters((prev) => prev.map((p, i) => (i === idx ? { ...p, ...patch } : p)));

  const addPrinter = () =>
    setPrinters((prev) => [...prev, { name: "", handles_kinds: [] }]);

  const removePrinter = (idx: number) =>
    setPrinters((prev) => prev.filter((_, i) => i !== idx));

  return (
    <div
      className="fixed inset-0 bg-black/40 backdrop-blur-sm z-50 flex items-center justify-center p-4"
      onClick={onClose}
    >
      <div
        className="card max-w-2xl w-full p-6 space-y-4 max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <header>
          <h2 className="text-lg">{t("printAgents.modalTitle")}</h2>
          <p className="text-text-muted text-sm mt-1">
            {t("printAgents.modalHint1")} <code className="font-mono">agent.toml</code>{" "}
            {t("printAgents.modalHint2")}
          </p>
        </header>

        <form
          onSubmit={(e) => {
            e.preventDefault();
            setErr(null);
            if (!name.trim()) {
              setErr(t("printAgents.errNameRequired"));
              return;
            }
            mut.mutate();
          }}
          className="space-y-3"
        >
          <Field label={t("printAgents.agentName")} hint={t("printAgents.agentNameHint")}>
            <input
              type="text"
              required
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="w-full px-3 py-2 rounded-md bg-surface border border-border text-sm"
              placeholder="PC-PROD-01"
            />
          </Field>

          <div>
            <h3 className="text-sm font-mono uppercase tracking-wider text-text-muted mb-2">
              {t("printAgents.printers")}
            </h3>
            <p className="text-text-muted text-xs mb-2">
              <strong>{t("printAgents.logicalName")}</strong> {t("printAgents.printersHint1")}{" "}
              <code className="font-mono">print_jobs.printer_name</code>. {t("printAgents.printersHint2")}{" "}
              <em>{t("printAgents.printersHint3")}</em> {t("printAgents.printersHint4")}{" "}
              <code className="font-mono">agent.toml</code> {t("printAgents.printersHint5")}{" "}
              <code className="font-mono">handles_kinds</code> {t("printAgents.printersHint6")}
            </p>
            <div className="space-y-2">
              {printers.map((p, idx) => (
                <div
                  key={idx}
                  className="card p-3 grid grid-cols-1 sm:grid-cols-[1fr_2fr_auto] gap-2 items-start"
                >
                  <input
                    type="text"
                    value={p.name}
                    onChange={(e) => updatePrinter(idx, { name: e.target.value })}
                    placeholder="laser-bw"
                    className="px-3 py-2 rounded-md bg-surface border border-border text-sm"
                  />
                  <input
                    type="text"
                    value={p.handles_kinds.join(", ")}
                    onChange={(e) =>
                      updatePrinter(idx, {
                        handles_kinds: e.target.value
                          .split(",")
                          .map((s) => s.trim())
                          .filter(Boolean),
                      })
                    }
                    placeholder="passport, packing-list"
                    className="px-3 py-2 rounded-md bg-surface border border-border text-xs font-mono"
                  />
                  <button
                    type="button"
                    onClick={() => removePrinter(idx)}
                    className="btn"
                    aria-label={t("printAgents.removePrinter")}
                    title={t("common.delete")}
                  >
                    ✕
                  </button>
                </div>
              ))}
              <button type="button" className="btn" onClick={addPrinter}>
                {t("printAgents.addPrinter")}
              </button>
            </div>
          </div>

          {err && <ErrorBanner onDismiss={() => setErr(null)}>{err}</ErrorBanner>}

          <div className="flex items-center justify-end gap-2 pt-2">
            <button type="button" className="btn" onClick={onClose}>
              {t("common.cancel")}
            </button>
            <button type="submit" className="btn btn-primary" disabled={mut.isPending}>
              {mut.isPending ? t("common.creating") : t("common.create")}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function TokenModal({
  data,
  onClose,
}: {
  data: PrintAgentCreateResponse;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(data.token);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Clipboard API blocked — fall through; the operator can still
      // copy manually from the visible <code> block below.
    }
  };

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="card max-w-2xl w-full p-6 space-y-4">
        <header>
          <h2 className="text-lg">{t("printAgents.tokenCreated")}</h2>
          <p className="text-text-muted text-sm mt-1">
            {t("printAgents.tokenWarning1")} <strong>{t("printAgents.tokenWarningBold")}</strong>{t("printAgents.tokenWarning2")}
          </p>
        </header>

        <section className="space-y-2">
          <Field label={t("printAgents.agentField")}>
            <p className="font-mono text-sm">
              {data.name} <span className="text-text-muted">({data.id})</span>
            </p>
          </Field>
          <Field label={t("printAgents.bearerToken")}>
            <div className="flex items-stretch gap-2">
              <code className="flex-1 px-3 py-2 rounded-md bg-surface border border-border text-xs font-mono break-all">
                {data.token}
              </code>
              <button type="button" className="btn" onClick={copy}>
                {copied ? t("printAgents.copied") : t("printAgents.copy")}
              </button>
            </div>
            <p className="text-text-muted text-xs mt-2">
              {t("printAgents.pasteInto")} <code className="font-mono">agent.toml</code> {t("printAgents.pasteField")}{" "}
              <code className="font-mono">auth_token</code>.
            </p>
          </Field>
        </section>

        <div className="flex items-center justify-end gap-2 pt-2">
          <button className="btn btn-primary" onClick={onClose}>
            {t("printAgents.savedClose")}
          </button>
        </div>
      </div>
    </div>
  );
}

function Field({
  label,
  children,
  hint,
}: {
  label: string;
  children: React.ReactNode;
  hint?: string;
}) {
  return (
    <label className="block">
      <span className="text-xs font-mono uppercase tracking-wider text-text-muted">{label}</span>
      <div className="mt-1">{children}</div>
      {hint && <p className="text-text-muted text-xs mt-1">{hint}</p>}
    </label>
  );
}

function isOnline(a: PrintAgentRead): boolean {
  if (!a.is_active || !a.last_seen_at) return false;
  const dt = new Date(a.last_seen_at).getTime();
  return Number.isFinite(dt) && Date.now() - dt < ONLINE_FRESH_SECONDS * 1000;
}

function formatRelative(iso: string, t: TFunction): string {
  const dt = new Date(iso).getTime();
  if (!Number.isFinite(dt)) return iso;
  const diff = Math.max(0, Math.round((Date.now() - dt) / 1000));
  if (diff < 60) return t("printAgents.secAgo", { n: diff });
  if (diff < 3600) return t("printAgents.minAgo", { n: Math.round(diff / 60) });
  if (diff < 86_400) return t("printAgents.hourAgo", { n: Math.round(diff / 3600) });
  return new Date(iso).toLocaleString();
}
